/**
 * Execution Risk reference (DC-1646 — was the full Execution Risk Card, DC-1367).
 *
 * The org-readiness metric is org-scoped and identical on every journey, so
 * re-rendering the full gauge inside a dataset-scoped view hid the axis
 * switch (the single most confusing thing in the readiness set per
 * docs/design/v2.0-readiness-dashboard-consolidation.md). One canonical
 * home per metric: the full dashboard lives at Readiness → Organizational;
 * this card is a compact LABELED REFERENCE that names the axis and links
 * there — never a re-rendered widget.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2 } from 'lucide-react';
import type { ExecutionRiskData } from './types';
import { getRiskColor, getRiskLabel } from './helpers';

interface ExecutionRiskCardProps {
  risk: ExecutionRiskData | null;
}

export const ExecutionRiskCard: React.FC<ExecutionRiskCardProps> = ({ risk }) => {
  const riskLevel = risk?.risk_level || 'NOT_CONFIGURED';
  const riskColor = getRiskColor(riskLevel);
  const pct = risk?.combined_factor != null ? `${Math.round(risk.combined_factor * 100)}%` : '--';

  return (
    <div className="org-readiness-ref">
      <div className="org-readiness-ref__eyebrow">
        <Building2 size={13} aria-hidden="true" />
        <span>Organizational readiness · org-wide</span>
      </div>
      <div className="org-readiness-ref__reading">
        <span className="org-readiness-ref__value">{pct}</span>
        <span className="org-readiness-ref__level" style={{ color: riskColor }}>
          {getRiskLabel(riskLevel)}
        </span>
      </div>
      <p className="org-readiness-ref__note">
        Execution risk is org-scoped — the same reading for every dataset. The full breakdown
        lives in its one canonical home.
      </p>
      <Link to="/readiness/organizational" className="org-readiness-ref__link">
        View Execution Risk
        <ArrowRight size={14} aria-hidden="true" />
      </Link>
    </div>
  );
};
